import { message } from 'antd';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import InspectionsServices from '../../../services/InspectionsServices';
import { putPublicFileToBucket } from '../../../helpers/manageUpload';

const MAX_FILE_SIZE_MB = 15;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

const normalizePhotoValue = (rawValue) => {
  if (Array.isArray(rawValue)) return rawValue.filter(Boolean);
  if (typeof rawValue !== 'string') return [];

  return rawValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

const getResponseSignedUrls = (responseBody) => {
  if (responseBody?.documentsUpdated) {
    if (Array.isArray(responseBody.documentsUpdated.url)) return responseBody.documentsUpdated.url.filter(Boolean);
    if (Array.isArray(responseBody.documentsUpdated.urls)) return responseBody.documentsUpdated.urls.filter(Boolean);
    if (typeof responseBody.documentsUpdated.url === 'string' && responseBody.documentsUpdated.url.trim()) return [responseBody.documentsUpdated.url];
    if (typeof responseBody.documentsUpdated.urls === 'string' && responseBody.documentsUpdated.urls.trim()) return [responseBody.documentsUpdated.urls];
  }

  if (Array.isArray(responseBody?.url)) return responseBody.url.filter(Boolean);
  if (Array.isArray(responseBody?.urls)) return responseBody.urls.filter(Boolean);
  if (typeof responseBody?.url === 'string' && responseBody.url.trim()) return [responseBody.url];
  if (typeof responseBody?.urls === 'string' && responseBody.urls.trim()) return [responseBody.urls];

  return [];
};

const PhotoBox = ({ value, inspectionId, sectionId, checkpoint, reload }) => {
  const [isUploading, setIsUploading] = useState(false);

  const photos = useMemo(() => {
    const normalizedValue = normalizePhotoValue(value);
    if (normalizedValue.length) return normalizedValue;

    const normalizedUri = normalizePhotoValue(checkpoint?.uri);
    if (normalizedUri.length) return normalizedUri;

    const normalizedCheckpointValue = normalizePhotoValue(checkpoint?.value);
    if (normalizedCheckpointValue.length) return normalizedCheckpointValue;

    return [];
  }, [value, checkpoint?.uri, checkpoint?.value]);

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return `Format non supporté (${file.name}).`;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) return `${file.name} dépasse ${MAX_FILE_SIZE_MB}MB.`;
    return null;
  };

  const uploadFilesThroughInspectionServices = async (files) => {
    const documentsInfos = files.map((file) => ({
      fileName: file.name,
      type: file.type,
    }));

    const res = await InspectionsServices.saveDocumentInfos(inspectionId, documentsInfos, true);
    if (res.status !== 200) {
      throw new Error((res.body && (res.body.error || res.body.message)) || 'Impossible de preparer l upload');
    }

    const signedUrls = getResponseSignedUrls(res?.body);

    if (!signedUrls.length) {
      throw new Error("Réponse d'upload invalide (aucune URL signée reçue)");
    }

    if (signedUrls.length !== files.length) {
      throw new Error(`Nombre d'URL signées inattendu (${signedUrls.length}/${files.length})`);
    }

    const uploadResults = await Promise.all(files.map((file, index) => putPublicFileToBucket(signedUrls[index], file.type, file)));

    const hasFailedUpload = uploadResults.some((result) => result?.status < 200 || result?.status >= 300);
    if (hasFailedUpload) {
      throw new Error("Echec de l'upload public sur S3 (ACL public-read).");
    }

    return signedUrls.map((url) => url.split('?')[0]);
  };

  const savePhotos = async (nextPhotos) => {
    const shouldSaveAsArray = Array.isArray(value) || Array.isArray(checkpoint?.value);
    const payloadValue = shouldSaveAsArray ? nextPhotos : nextPhotos[0] || '';

    const res = await InspectionsServices.updateCheckpoint(inspectionId, sectionId, checkpoint._id, {
      value: payloadValue,
    });

    if (res.status !== 200) throw new Error((res.body && (res.body.message || res.body.error)) || 'Erreur de sauvegarde');
  };

  const onUpload = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    if (files.length > 1) {
      message.warning('Un seul fichier est autorise pour ce point de controle photo.');
    }

    const selectedFile = files[0];
    const err = validateFile(selectedFile);
    if (err) {
      message.warning(err);
      input.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadedUrls = await uploadFilesThroughInspectionServices([selectedFile]);
      await savePhotos(uploadedUrls);
      message.success(photos.length > 0 ? 'Photo remplacee' : 'Photo ajoutee');
      if (reload) reload();
    } catch (e) {
      message.error(`Erreur: ${e.message || e}`);
    } finally {
      setIsUploading(false);
      input.value = '';
    }
  };

  const onDelete = async (index) => {
    try {
      const next = photos.filter((_, i) => i !== index);
      await savePhotos(next);
      message.success('Photo supprimee');
      if (reload) reload();
    } catch (e) {
      message.error(`Erreur: ${e.message || e}`);
    }
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <input type='file' accept='image/*' disabled={isUploading} onChange={onUpload} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
        {photos.map((url, i) => (
          <div key={`${url}-${i}`} style={{ width: 120 }}>
            <a href={url} target='_blank' rel='noreferrer noopener'>
              <img src={url} alt={`inspection-${i}`} style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
            </a>
            <button type='button' onClick={() => onDelete(i)} style={{ width: '100%', marginTop: 4 }}>
              Supprimer
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

PhotoBox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  inspectionId: PropTypes.string,
  sectionId: PropTypes.string,
  checkpoint: PropTypes.object,
  reload: PropTypes.func,
};

export default PhotoBox;
